import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function DuluxPaintersEcosystemPage() {
  useSEO({
    title: 'Dulux Painters Ecosystem Strategy | Design For',
    description: 'Strategic consulting for trade professional engagement. Research-driven strategy for B2B customer experience and ecosystem development.',
    keywords: 'trade professionals, B2B strategy, customer ecosystem, painter engagement, construction industry',
    canonical: '/work/dulux-painters-ecosystem',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Strategic consulting for trade professional engagement',
    description: 'We partnered with Dulux to develop strategic frameworks for painter ecosystem development, trade professional engagement, and B2B customer experience.',
    url: 'https://example.com/work/dulux-painters-ecosystem',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Paint company - Trade professional ecosystem',
    keywords: 'trade professionals, B2B strategy, customer ecosystem, painter engagement, construction industry',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/work"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Transforming the painters ecosystem across digital &amp; retail
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">Dulux</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                Dulux&apos;s trade business sits at the centre of a complex ecosystem: painters, builders, specifiers, store teams, reps, and suppliers — yet the experience had evolved in fragmented ways over time. While Dulux was a strong brand, the organisation lacked a shared, end to end understanding of how trade customers actually worked, where time and money were lost, and which relationships truly influenced behaviour and loyalty.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                Working closely with the Dulux Trade team, we set out to create a single, holistic view of the trade customer ecosystem and use it to reframe priorities across service, digital, in store experience, and relationship management.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Conducted 30+ hours of contextual research with 31 participants across 10+ trade professions',
                  'Ran 7 collaborative workshops with Dulux teams to build shared understanding and buy in',
                  'Mapped the end to end trade service ecosystem, showing interactions between customers, reps, stores, and systems',
                  'Synthesised insight into clear themes and opportunity areas, prioritised against customer and business value',
                  'Developed and tested concepts including click & collect, site delivery, lockers, single customer view, feedback tools, and repaint reminders',
                  'Delivered a set of prioritised opportunities and artefacts that Dulux teams could immediately take forward',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/806b5990f6b8f076533cd25370c837a9.jpeg"
                  alt="Transforming the painters ecosystem across digital and retail — Dulux"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ecd5c42ee7fa04c48f29fe6957d4f04e.png"
                  alt="Dulux Trade Current State Service Map"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="dulux-painters-ecosystem" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
